import { useEffect, useState } from 'react';
import type { ErrorResponse } from 'react-router';
import { useLocation } from 'react-router';
import { useMount } from 'ahooks';
import NotFound from '@modules/error/component/not-found/NotFound';
import ErrorPage from '@modules/error/page/ErrorPage';
import ErrorPageWrapper from '@modules/error/component/error-page-wrapper/ErrorPageWrapper';
import { isApError } from '~/modules/root/graphql/responses';
import Page403 from '~/modules/error/component/page-403/Page403';
import Page401 from '~/modules/error/component/page-401/Page401';
import Page410 from '~/modules/error/component/page-410/Page410';
import ServiceUnavailable from '~/modules/error/component/service-unavailable/ServiceUnavailable';
import { isToastableErrorBody } from '~/modules/error/util/is-toastable-error-body';
import { isCloudflareChallengeResponse } from '~/modules/error/util/is-cloudflare-challenge';
import { tryReloadOnCloudflareChallenge } from '~/modules/error/util/cloudflare-challenge-reload';
import { wasLastTransitionAMutation } from '~/modules/error/util/router-transition-origin';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { useMaybeAppData } from '~/modules/root';

type CloudflareChallengePhase = 'reloading' | 'manual';

/**
 * Status UI while recovering from a soft-nav Cloudflare challenge.
 * Explains the browser check — never Page403 and never “Something went wrong”.
 */
const CloudflareChallengePlaceholder = ({
    phase,
}: {
    phase: CloudflareChallengePhase;
}) => {
    const isReloading = phase === 'reloading';

    return (
        <ErrorPageWrapper>
            <div
                role="status"
                aria-live="polite"
                className="flex min-h-[50vh] flex-col items-center justify-center px-6 py-16 text-center"
            >
                <h1 className="text-2xl font-normal tracking-[-0.5px] text-gray-900 md:text-[2rem]">
                    {isReloading
                        ? 'Verifying your browser'
                        : 'Browser check needed'}
                </h1>
                <p className="mt-4 max-w-md text-base leading-relaxed text-gray-700 md:text-lg">
                    {isReloading
                        ? 'A security check interrupted this page. Refreshing so you can continue…'
                        : 'A security check interrupted this page. Refresh to continue — you may see a short verification step.'}
                </p>
                {!isReloading ? (
                    <button
                        type="button"
                        className="mt-8 rounded-full bg-brick-500 px-6 py-2.5 text-sm text-white transition-colors hover:bg-brick-600 md:px-9 md:py-3 md:text-base"
                        onClick={() => {
                            window.location.reload();
                        }}
                    >
                        Refresh page
                    </button>
                ) : null}
            </div>
        </ErrorPageWrapper>
    );
};

/**
 * Soft-nav Cloudflare bot challenges arrive as ErrorResponse 403 + challenge
 * HTML. Hard-reload so Cloudflare can serve its own challenge document (with a
 * working CTA). If the cooldown blocks another reload — or auto-reload is not
 * allowed (e.g. form action) — keep a clear recovery UI with a manual refresh
 * — never Page403 or a generic SWW page.
 */
const CloudflareChallengeRecovery = ({
    caught,
    allowAutoReload,
}: {
    caught: ErrorResponse;
    allowAutoReload: boolean;
}) => {
    const [phase, setPhase] = useState<CloudflareChallengePhase>(
        allowAutoReload ? 'reloading' : 'manual',
    );

    useMount(() => {
        if (
            tryReloadOnCloudflareChallenge(caught.status, caught.data, {
                allowAutoReload,
            })
        ) {
            return;
        }
        if (allowAutoReload) {
            console.warn(
                '[CatchPage] Cloudflare bot challenge — reload blocked (cooldown or storage unavailable); offering manual refresh',
            );
        }
        setPhase('manual');
    });

    return <CloudflareChallengePlaceholder phase={phase} />;
};

/**
 * The message a loader attached to its thrown response, as
 * `data({ message }, init)` sends it. Only that object shape qualifies: a
 * string body on a 5xx is an edge or proxy document, not copy for the page.
 */
const readThrownMessage = (data: unknown): string | undefined => {
    if (!data || typeof data !== 'object' || !('message' in data)) {
        return undefined;
    }
    return typeof data.message === 'string' ? data.message : undefined;
};

/**
 * This component should be exported from a Page that is the outlet of
 * the root Page. It will render between top nav and footer
 */
export const CatchPage = ({ caught }: { caught: ErrorResponse }) => {
    const maybeAppData = useMaybeAppData();
    const user = maybeAppData?.user;
    const location = useLocation();

    const { toast } = useToaster();
    const isCloudflareChallenge = isCloudflareChallengeResponse(
        caught.status,
        caught.data,
    );
    // Initial document / SSR-hydrated errors use React Router's `default` location
    // key. Soft-nav (and other client transitions) assign a generated key.
    const isDocumentOriginError = location.key === 'default';
    const allowCloudflareAutoReload =
        !isDocumentOriginError && !wasLastTransitionAMutation();

    useEffect(() => {
        if (isCloudflareChallenge) {
            return;
        }
        if (
            typeof caught.data === 'string' &&
            isToastableErrorBody(caught.status, caught.data)
        ) {
            toast(caught.data);
        }
    }, [caught.data, caught.status, isCloudflareChallenge, toast]);

    switch (caught.status) {
        case 404:
            return (
                <ErrorPageWrapper>
                    <NotFound />
                </ErrorPageWrapper>
            );
        case 403:
            if (isCloudflareChallenge) {
                // Document/SSR-origin challenges: hard-reload loops against the
                // same edge check. Fall through to Page403 instead of recovery.
                if (isDocumentOriginError) {
                    return (
                        <ErrorPageWrapper>
                            <Page403 user={user} />
                        </ErrorPageWrapper>
                    );
                }
                return (
                    <CloudflareChallengeRecovery
                        caught={caught}
                        allowAutoReload={allowCloudflareAutoReload}
                    />
                );
            }
            return (
                <ErrorPageWrapper>
                    <Page403 user={user} />
                </ErrorPageWrapper>
            );
        case 401:
            return (
                <ErrorPageWrapper>
                    <Page401 />
                </ErrorPageWrapper>
            );
        case 410:
            return (
                <ErrorPageWrapper>
                    <Page410 />
                </ErrorPageWrapper>
            );
        case 503:
            // The message a loader attaches to the 503 reaches the visitor
            // verbatim, so a loader sends copy meant for them or nothing.
            return (
                <ErrorPageWrapper>
                    <ServiceUnavailable
                        message={readThrownMessage(caught.data)}
                    />
                </ErrorPageWrapper>
            );
    }

    const { status, statusText, data } = caught;

    let message: string = statusText;
    if (isApError(data)) {
        message = data.message;
    }
    message = message || 'Unknown error';

    const error = new Error(`${status}: ${message}`);

    /**
     * add special content for other error types such as
     * 500 or 403 401
     */
    return <ErrorPage error={error} />;
};
